import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import { Trophy, Medal, Award, Share2, Download, Loader2, Users, Globe } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import { FanionBadge } from "@/components/FanionBadge";

export default function Leaderboard() {
  const [rowsLeague, setRowsLeague] = useState([]);
  const [rowsGlobal, setRowsGlobal] = useState([]);
  const [tab, setTab] = useState("league");
  const { user } = useAuth();
  const shareRef = useRef(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const global = await api.get("/leaderboard/global");
        setRowsGlobal(global.data);
      } catch (err) { console.warn("Failed to load global leaderboard:", err); }
      if (user?.league_id) {
        try {
          const league = await api.get("/leaderboard");
          setRowsLeague(league.data);
        } catch (err) { console.warn("Failed to load league leaderboard:", err); }
      }
    })();
  }, [user]);

  const rows = tab === "league" ? rowsLeague : rowsGlobal;

  const generateImage = async () => {
    if (!shareRef.current) return null;
    return toPng(shareRef.current, {
      cacheBust: true,
      pixelRatio: 2,
      backgroundColor: "#0A2540",
    });
  };

  const doShare = async () => {
    setSharing(true);
    try {
      const dataUrl = await generateImage();
      if (!dataUrl) return;
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], "classement-yoles.png", { type: "image/png" });
      const text = `🏆 Classement Tour des Yoles${tab === "league" && user?.league_name ? ` — ${user.league_name}` : " — Général"}`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "Classement", text });
      } else if (navigator.share) {
        await navigator.share({ title: "Classement", text });
      } else {
        const wa = `https://wa.me/?text=${encodeURIComponent(text + " " + window.location.href)}`;
        window.open(wa, "_blank");
      }
    } catch (e) { /* ignore */ }
    finally { setSharing(false); }
  };

  const doDownload = async () => {
    setSharing(true);
    try {
      const dataUrl = await generateImage();
      if (!dataUrl) return;
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = "classement-tour-des-yoles.png";
      a.click();
      toast.success("Image téléchargée");
    } catch (e) { toast.error("Impossible de générer l'image"); }
    finally { setSharing(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-[color:var(--deep)]">
            Classement
          </h1>
          <p className="text-sm text-slate-500 mt-2">
            Compare-toi à ta ligue ou à tous les parieurs.
          </p>
        </div>
        {rows.length > 0 && (
          <div className="flex gap-2">
            <Button
              onClick={doDownload}
              disabled={sharing}
              variant="outline"
              data-testid="download-leaderboard"
              className="rounded-full h-9 text-xs font-bold"
            >
              {sharing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Download className="h-3.5 w-3.5 mr-1" />}
              Image
            </Button>
            <Button
              onClick={doShare}
              disabled={sharing}
              data-testid="share-leaderboard"
              className="rounded-full h-9 text-xs font-bold bg-[color:var(--palm)] hover:bg-[color:var(--palm)]/90 text-[color:var(--deep)] btn-glow"
            >
              {sharing ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Share2 className="h-3.5 w-3.5 mr-1" />}
              Partager
            </Button>
          </div>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-slate-100 p-1 rounded-full h-auto">
          <TabsTrigger value="league" data-testid="lb-tab-league" disabled={!user?.league_id} className="rounded-full px-4 py-2 text-xs font-bold data-[state=active]:bg-white data-[state=active]:text-[color:var(--coral)]">
            <Users className="h-3.5 w-3.5 mr-1.5" />
            {user?.league_name || "Ma ligue"}
          </TabsTrigger>
          <TabsTrigger value="global" data-testid="lb-tab-global" className="rounded-full px-4 py-2 text-xs font-bold data-[state=active]:bg-white data-[state=active]:text-[color:var(--coral)]">
            <Globe className="h-3.5 w-3.5 mr-1.5" /> Général
          </TabsTrigger>
        </TabsList>

        <TabsContent value={tab} className="mt-6 space-y-6">
          {tab === "league" && !user?.league_id && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-900">
              Rejoins une ligue depuis <a href="/profil" className="font-bold underline">ton profil</a> pour voir ce classement.
            </div>
          )}

          {rows.length >= 3 && (
            <section className="grid grid-cols-3 gap-3">
              <PodiumTile row={rows[1]} rank={2} height="h-28" icon={Medal} color="bg-slate-200 text-slate-700" you={user?.id === rows[1].user_id} />
              <PodiumTile row={rows[0]} rank={1} height="h-40" icon={Trophy} color="bg-[color:var(--sunshine)] text-[color:var(--deep)]" you={user?.id === rows[0].user_id} />
              <PodiumTile row={rows[2]} rank={3} height="h-24" icon={Award} color="bg-orange-200 text-orange-800" you={user?.id === rows[2].user_id} />
            </section>
          )}

          <section className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden">
            {rows.length === 0 && (
              <div className="p-10 text-center text-sm text-slate-500">Personne au classement pour l'instant.</div>
            )}
            {rows.map((row, i) => {
              const isYou = row.user_id === user?.id;
              return (
                <motion.div
                  key={row.user_id}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className={`flex items-center gap-4 px-5 py-4 ${isYou ? "bg-[color:var(--sunshine)]/10" : ""}`}
                  data-testid={`lb-row-${row.user_id}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-mono font-black text-sm flex-shrink-0 ${
                    i === 0 ? "bg-[color:var(--sunshine)] text-[color:var(--deep)]" :
                    i === 1 ? "bg-slate-200 text-slate-700" :
                    i === 2 ? "bg-orange-200 text-orange-800" :
                    "bg-slate-100 text-slate-500"
                  }`}>
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm flex items-center gap-2 truncate">
                      <FanionBadge level={row.fanion_level || 1} size="sm" />
                      {row.name}
                      {isYou && <span className="text-[10px] font-black uppercase text-[color:var(--coral)] bg-red-50 border border-red-100 rounded-full px-1.5 py-0.5">Toi</span>}
                    </div>
                    <div className="text-[11px] text-slate-500">
                      {row.fanion_label || "Mousse"} · {row.won_bets}/{row.total_bets} pronostics classés
                    </div>
                  </div>
                  <div className="font-mono font-bold tabular-nums text-[color:var(--deep)]">
                    {row.wallet_points}<span className="text-xs opacity-60 ml-1">pts</span>
                  </div>
                </motion.div>
              );
            })}
          </section>
        </TabsContent>
      </Tabs>

      {/* Off-screen share card */}
      <div style={{ position: "fixed", top: "-9999px", left: "-9999px" }}>
        <div
          ref={shareRef}
          style={{ width: 720, padding: 40, background: "#0A2540", color: "white", fontFamily: "Outfit, sans-serif" }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 32 }}>
            <div>
              <div style={{ fontSize: 11, letterSpacing: 3, textTransform: "uppercase", color: "#FFC107", fontWeight: 800 }}>
                Tour des Yoles · Martinique
              </div>
              <div style={{ fontSize: 40, fontWeight: 900, lineHeight: 1.05, marginTop: 8 }}>
                Classement
              </div>
              <div style={{ fontSize: 16, opacity: 0.85, marginTop: 4 }}>
                {tab === "league" && user?.league_name ? user.league_name : "Général"}
              </div>
            </div>
            <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#FF5A5F", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, fontWeight: 900 }}>
              ⛵
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {rows.slice(0, 10).map((row, i) => (
              <div key={row.user_id} style={{
                display: "flex", alignItems: "center", gap: 16,
                background: i === 0 ? "rgba(255,193,7,0.15)" : "rgba(255,255,255,0.05)",
                border: i === 0 ? "1px solid #FFC107" : "1px solid rgba(255,255,255,0.08)",
                borderRadius: 16, padding: "14px 20px",
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: "50%",
                  background: i === 0 ? "#FFC107" : i === 1 ? "#CBD5E1" : i === 2 ? "#FDBA74" : "rgba(255,255,255,0.1)",
                  color: i < 3 ? "#0A2540" : "white",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontFamily: "JetBrains Mono, monospace", fontWeight: 900, fontSize: 16,
                }}>{i + 1}</div>
                <div style={{ flex: 1, fontWeight: 700, fontSize: 18 }}>{row.name}</div>
                <div style={{ fontFamily: "JetBrains Mono, monospace", fontWeight: 800, fontSize: 20 }}>
                  {row.wallet_points}<span style={{ fontSize: 12, opacity: 0.6, marginLeft: 4 }}>pts</span>
                </div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 24, textAlign: "right", fontSize: 11, opacity: 0.5, letterSpacing: 1 }}>
            {new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
          </div>
        </div>
      </div>
    </div>
  );
}

function PodiumTile({ row, rank, height, icon: Icon, color, you }) {
  return (
    <div className="flex flex-col items-center justify-end" data-testid={`podium-${rank}`}>
      <div className={`w-12 h-12 rounded-full ${color} flex items-center justify-center mb-2 ring-4 ring-white shadow-lg`}>
        <Icon className="h-6 w-6" strokeWidth={2.4} />
      </div>
      <div className="text-center font-bold text-sm truncate max-w-full px-1">{row.name}</div>
      <div className="text-xs font-mono text-slate-500">{row.wallet_points} pts</div>
      <div className={`mt-2 w-full ${height} rounded-t-2xl ${color} flex items-start justify-center pt-2 font-display font-black text-2xl`}>
        {rank}
      </div>
      {you && <div className="text-[10px] font-black uppercase text-[color:var(--coral)] mt-1">Toi</div>}
    </div>
  );
}
